import React, { useState, useEffect, useRef } from 'react';
import Editor, { DiffEditor } from '@monaco-editor/react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Typography
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PaletteIcon from '@mui/icons-material/Palette';

const MonacoEditor = ({ open, onClose, initialValue, onSave, language = 'json', title = 'Editor de Código', sidebarContent, onEditorReady }) => {
  const [value, setValue] = useState(initialValue || '');
  const [originalValue, setOriginalValue] = useState(initialValue || '');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [theme, setTheme] = useState('vs-dark');
  const [showDiff, setShowDiff] = useState(false);
  // mountedMode: 'editor' | 'diff' | null (null = desmontado brevemente para evitar condição de corrida)
  const [mountedMode, setMountedMode] = useState('editor');
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const fullscreen = true;
  const editorRef = useRef(null);
  const diffEditorRef = useRef(null);
  const hasSidebar = !!sidebarContent;

  // Função para inserir texto na posição do cursor
  const insertTextAtCursor = (text) => {
    if (editorRef.current) {
      const editor = editorRef.current;
      const selection = editor.getSelection();
      const range = new window.monaco.Range(
        selection.startLineNumber,
        selection.startColumn,
        selection.endLineNumber,
        selection.endColumn
      );
      
      const op = { range: range, text: text, forceMoveMarkers: true };
      editor.executeEdits('insert-text', [op]);
      
      // Atualiza o valor do estado
      setValue(editor.getValue());
      
      // Coloca o foco de volta no editor
      editor.focus();
    }
  };

  // Expõe a função de inserção quando o editor estiver pronto
  useEffect(() => {
    if (onEditorReady && editorRef.current) {
      onEditorReady(insertTextAtCursor);
    }
  }, [editorRef.current, onEditorReady]);

  const themes = [
    { value: 'vs-light', label: 'Claro' },
    { value: 'vs-dark', label: 'Escuro' },
    { value: 'hc-black', label: 'Alto Contraste' }
  ];

  useEffect(() => {
    if (open && initialValue) {
      setValue(initialValue ?? '');
      setOriginalValue(initialValue ?? '');
    }
  }, [open, initialValue]);
  useEffect(() => {
    // desmontagem breve para garantir que o Monaco limpe os modelos antes de remontar
    let timer = null;
    setMountedMode(null);
    timer = setTimeout(() => setMountedMode(showDiff ? 'diff' : 'editor'), 50);
    return () => timer && clearTimeout(timer);
  }, [showDiff]);

  const handleSave = () => {
    try {
      // Validação básica para JSON
      if (language === 'json') {
        JSON.parse(value);
      }
      onSave(value);
      setOriginalValue(value);
      setSnackbar({ open: true, message: 'Salvo com sucesso!', severity: 'success' });
      onClose();
    } catch (error) {
      setSnackbar({ open: true, message: `Erro: ${error.message}`, severity: 'error' });
    }
  };

  const handleClose = () => {
    // Verifica se há alterações não salvas
    if (hasChanges) {
      setShowConfirmClose(true);
      return;
    }
    // Se não há alterações, fecha normalmente
    performClose();
  };

  const performClose = () => {
    setValue(originalValue);
    setShowDiff(false);
    setMountedMode('editor');
    setShowConfirmClose(false);
    onClose();
  };

  const handleConfirmClose = () => {
    performClose();
  };

  const handleCancelClose = () => {
    setShowConfirmClose(false);
  };

  const hasChanges = value !== originalValue;

  // manipuladores
  const disposeEditorIfAny = () => {
    try {
      if (editorRef.current) {
        editorRef.current.dispose?.();
        editorRef.current = null;
      }
    } catch (e) {
      console.warn('Erro ao descartar editor', e);
    }
  };

  const disposeDiffIfAny = () => {
    try {
      if (diffEditorRef.current) {
        diffEditorRef.current.dispose?.();
        diffEditorRef.current = null;
      }
    } catch (e) {
      console.warn('Erro ao descartar editor de diferenças', e);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={fullscreen ? false : "lg"}
        fullWidth={!fullscreen}
        fullScreen={fullscreen}
        PaperProps={{
          sx: {
            height: fullscreen ? '100vh' : '80vh',
            maxHeight: fullscreen ? '100vh' : '80vh'
          }
        }}
        onKeyDown={(e) => {
          // Intercepta ESC para verificar alterações
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            handleClose();
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}> 
            <Box sx={{ display: 'flex', flexDirection: 'column', mr: 1 }}>
              {/* Título com ícone */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PaletteIcon sx={{ fontSize: 22, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
              </Box>
              {/* Subtítulo com linguagem/tema */}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
                {language?.toUpperCase?.() ?? language} — Tema: {themes.find(t => t.value === theme)?.label ?? theme}
              </Typography>
            </Box>
          </Box>          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Tema</InputLabel>
              <Select
                value={theme}
                label="Tema"
                onChange={(e) => setTheme(e.target.value)}
                startAdornment={<PaletteIcon sx={{ mr: 1 }} />}
              >
                {themes.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Mostrar Diferenças">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    disposeEditorIfAny();
                    setShowDiff(true);
                  }}
                  disabled={showDiff || !hasChanges}
                >
                  Diff
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Voltar ao Editor">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    disposeDiffIfAny();
                    setShowDiff(false);
                  }}
                  disabled={!showDiff}
                >
                  Editor
                </Button>
              </span>
            </Tooltip>
            {/* <Tooltip title={fullscreen ? "Sair da Tela Cheia" : "Tela Cheia"}>
              <IconButton onClick={ () => setFullscreen(!fullscreen)}>
                {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip> */}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'row' }}>
          {/* Sidebar à esquerda se fornecido */}
          {hasSidebar && (
            <Box sx={{ 
              width: '380px', 
              borderRight: '2px solid #e0e0e0', 
              overflowY: 'auto',
              p: 2.5,
              backgroundColor: theme === 'vs-dark' ? '#252526' : '#f5f5f5',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: theme === 'vs-dark' ? '#1e1e1e' : '#e0e0e0',
              },
              '&::-webkit-scrollbar-thumb': {
                background: theme === 'vs-dark' ? '#424242' : '#bdbdbd',
                borderRadius: '4px',
                '&:hover': {
                  background: theme === 'vs-dark' ? '#4e4e4e' : '#9e9e9e',
                }
              },
            }}>
              {sidebarContent}
            </Box>
          )}
          
          {/* Editor */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {mountedMode === 'diff' ? (
              <DiffEditor
                height="100%"
                language={language}
                // forçar remontagem ao alternar entre diff/editor usando chave
                key={`diff-${language}-${theme}`}
                original={String(originalValue ?? '')}
                modified={String(value ?? '')}
                theme={theme}
                options={{
                  minimap: { enabled: true },
                  fontSize: 14,
                  wordWrap: 'on',
                  automaticLayout: true,
                  renderSideBySide: true,
                  readOnly: true,
                  scrollBeyondLastLine: false,
                  smoothScrolling: true,
                  cursorBlinking: 'blink',
                  contextmenu: true,
                  mouseWheelZoom: true,
                  selectOnLineNumbers: true,
                  roundedSelection: false,
                }}
                onMount={(diffEditorInstance) => {
                  try {
                    diffEditorRef.current = diffEditorInstance;
                  } catch (e) {
                    console.warn('Falha ao definir diffEditorRef', e);
                  }
                }}
              />
            ) : mountedMode === 'editor' ? (
              <Editor
                height="100%"
              language={language}
              // forçar remontagem ao alternar entre diff/editor usando chave
              key={`editor-${language}-${theme}`}
              value={String(value ?? '')}
              onChange={(val) => setValue(val ?? '')}
              theme={theme}
              options={{
                minimap: { enabled: true },
                fontSize: 14,
                wordWrap: 'on',
                automaticLayout: true,
                scrollBeyondLastLine: false,
                smoothScrolling: true,
                cursorBlinking: 'blink',
                contextmenu: true,
                mouseWheelZoom: true,
                selectOnLineNumbers: true,
                roundedSelection: false,
                lineNumbers: 'on',
                glyphMargin: true,
                folding: true,
                lineDecorationsWidth: 10,
                lineNumbersMinChars: 3,
              }}
              onMount={(editorInstance) => {
                try {
                  editorRef.current = editorInstance;
                } catch (e) {
                  console.warn('Falha ao definir editorRef', e);
                }
              }}
            />
          ) : null}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 'auto' }}>
            {hasChanges && (
              <Typography variant="body2" color="warning.main">
                Você tem mudanças não salvas
              </Typography>
            )}
          </Box>
          <Button
            onClick={handleClose}
            startIcon={<CancelIcon />}
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            startIcon={<SaveIcon />}
            variant="contained"
            disabled={!hasChanges}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Modal de confirmação para fechar com alterações não salvas */}
      <Dialog
        open={showConfirmClose}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Alterações não salvas</DialogTitle>
        <DialogContent>
          <Typography>
            Você tem alterações não salvas. Deseja realmente fechar o editor sem salvar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose} variant="outlined">
            Continuar Editando
          </Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            Fechar sem Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MonacoEditor;
